/**
 * HrEvaluationModal — add or edit a member evaluation.
 *
 * Fields: date, evaluator (any member, or none), strengths, weaknesses, goals,
 * employee feedback. On edit it sends expected_updated_at for concurrent-edit
 * protection (§7).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select, type SelectOption } from '../../ui/Select';
import { hrService, type EvaluationPayload } from '../../../services/hr.service';
import { membersService } from '../../../services/members.service';
import { useToast } from '../../../hooks/useToast';
import { handleApiError } from '../../../lib/axios';
import type { MemberEvaluation } from '../../../types';

const textareaClass =
  'block w-full rounded-md border-strong shadow-sm focus:border-focus focus:ring-blue-500 text-sm text-heading bg-surface px-3 py-2 border';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  memberId: number;
  evaluation?: MemberEvaluation | null;
}

export function HrEvaluationModal({ isOpen, onClose, memberId, evaluation }: Props) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [date, setDate] = useState('');
  const [evaluatorId, setEvaluatorId] = useState('');
  const [strengths, setStrengths] = useState('');
  const [weaknesses, setWeaknesses] = useState('');
  const [goals, setGoals] = useState('');
  const [feedback, setFeedback] = useState('');

  // Members for the evaluator dropdown (only fetched while the modal is open).
  const { data: members = [] } = useQuery({
    queryKey: ['members-all-for-eval'],
    queryFn: () => membersService.getAll(),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const evaluatorOptions = useMemo<SelectOption[]>(
    () => [
      { value: '', label: '— None —' },
      ...members.map((m) => ({ value: String(m.id), label: `${m.first_name} ${m.last_name}` })),
    ],
    [members]
  );

  useEffect(() => {
    if (!isOpen) return;
    setDate(evaluation?.evaluation_date ?? '');
    setEvaluatorId(evaluation?.evaluator_id ? String(evaluation.evaluator_id) : '');
    setStrengths(evaluation?.strengths ?? '');
    setWeaknesses(evaluation?.weaknesses ?? '');
    setGoals(evaluation?.goals ?? '');
    setFeedback(evaluation?.employee_feedback ?? '');
  }, [isOpen, evaluation]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: EvaluationPayload = {
        evaluation_date: date,
        evaluator_id: evaluatorId ? Number(evaluatorId) : null,
        strengths: strengths || null,
        weaknesses: weaknesses || null,
        goals: goals || null,
        employee_feedback: feedback || null,
      };
      if (evaluation) {
        payload.expected_updated_at = evaluation.updated_at;
        return hrService.updateEvaluation(memberId, evaluation.id, payload);
      }
      return hrService.addEvaluation(memberId, payload);
    },
    onSuccess: () => {
      toast(evaluation ? 'Evaluation updated' : 'Evaluation added', 'success');
      queryClient.invalidateQueries({ queryKey: ['member-evaluations', memberId] });
      onClose();
    },
    onError: (err) => toast(handleApiError(err), 'error'),
  });

  const canSave = date !== '';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={evaluation ? 'Edit Evaluation' : 'Add Evaluation'} size="lg">
      <form
        onSubmit={(e) => { e.preventDefault(); if (canSave) mutation.mutate(); }}
        className="space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="Evaluation Date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} />
          <Select
            label="Evaluator"
            options={evaluatorOptions}
            value={evaluatorId}
            onChange={(e) => setEvaluatorId(e.target.value)}
            placeholder="— None —"
          />
        </div>
        {([
          ['Strengths', strengths, setStrengths],
          ['Weaknesses', weaknesses, setWeaknesses],
          ['Goals', goals, setGoals],
          ['Employee Feedback', feedback, setFeedback],
        ] as const).map(([label, value, setter]) => (
          <div key={label}>
            <label className="block text-sm font-medium text-body mb-1">{label}</label>
            <textarea className={textareaClass} rows={3} value={value} onChange={(e) => setter(e.target.value)} />
          </div>
        ))}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={mutation.isPending} disabled={!canSave}>
            {evaluation ? 'Save' : 'Add'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
